import React from 'react';
import {
  Drawer as MuiDrawer,
  DrawerProps as MuiDrawerProps,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const DRAWER_WIDTH = 280;
const MINI_DRAWER_WIDTH = 72;

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'styleVariant',
})<{ styleVariant?: 'default' | 'mini' | 'rail' }>(
  ({ theme, styleVariant = 'default' }) => ({
    width: styleVariant === 'mini' ? MINI_DRAWER_WIDTH : DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': {
      width: styleVariant === 'mini' ? MINI_DRAWER_WIDTH : DRAWER_WIDTH,
      transition: theme.transitions.create(['width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      overflowX: 'hidden',
      ...(styleVariant === 'mini' && {
        width: MINI_DRAWER_WIDTH,
        '& .MuiListItemText-root': {
          opacity: 0,
        },
        '& .MuiListItemIcon-root': {
          minWidth: 0,
          mr: 'auto',
          justifyContent: 'center',
        },
      }),
      ...(styleVariant === 'rail' && {
        '&:hover': {
          width: DRAWER_WIDTH,
          '& .MuiListItemText-root': {
            opacity: 1,
          },
          '& .MuiListItemIcon-root': {
            minWidth: 56,
            mr: 3,
          },
        },
        '& .MuiListItemText-root': {
          opacity: 0,
        },
        '& .MuiListItemIcon-root': {
          minWidth: 0,
          mr: 'auto',
          justifyContent: 'center',
        },
      }),
    },
  })
);

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const StyledListItemButton = styled(ListItemButton)<{ level?: number; selected?: boolean }>(
  ({ theme, level = 0, selected = false }) => ({
    paddingLeft: theme.spacing(2 + level * 2),
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.25, 1),
    ...(selected && {
      backgroundColor: theme.palette.primary.main + '15',
      color: theme.palette.primary.main,
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.main,
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.main + '20',
      },
    }),
  })
);

export interface DrawerItem {
  /**
   * Unique identifier
   */
  id: string;
  /**
   * Display label
   */
  label: string;
  /**
   * Icon component
   */
  icon?: React.ReactNode;
  /**
   * Destination path/route
   */
  path?: string;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Whether item is selected/active
   */
  selected?: boolean;
  /**
   * Whether item is disabled
   */
  disabled?: boolean;
  /**
   * Nested children items
   */
  children?: DrawerItem[];
  /**
   * Whether children are expanded by default
   */
  defaultExpanded?: boolean;
  /**
   * Badge content
   */
  badge?: string | number;
  /**
   * Whether to show divider after item
   */
  divider?: boolean;
}

export interface DrawerProps extends Omit<MuiDrawerProps, 'variant'> {
  /**
   * Whether drawer is open
   */
  open: boolean;
  /**
   * Callback fired when drawer should close
   */
  onClose?: () => void;
  /**
   * Callback fired when drawer should toggle
   */
  onToggle?: () => void;
  /**
   * Drawer variant
   */
  variant?: 'temporary' | 'permanent' | 'persistent';
  /**
   * Visual style variant
   */
  styleVariant?: 'default' | 'mini' | 'rail';
  /**
   * Drawer anchor position
   */
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  /**
   * Navigation items
   */
  items: DrawerItem[];
  /**
   * Header content
   */
  header?: React.ReactNode;
  /**
   * Footer content
   */
  footer?: React.ReactNode;
  /**
   * Whether to show close button in header
   */
  showCloseButton?: boolean;
  /**
   * Custom width
   */
  width?: number;
  /**
   * Whether to auto-close on mobile
   */
  autoCloseMobile?: boolean;
  /**
   * Callback fired when item is clicked
   */
  onItemClick?: (item: DrawerItem) => void;
}

const renderDrawerItem = (
  item: DrawerItem,
  level: number = 0,
  onItemClick?: (item: DrawerItem) => void,
  styleVariant?: 'default' | 'mini' | 'rail'
) => {
  const [expanded, setExpanded] = React.useState(item.defaultExpanded || false);
  const hasChildren = item.children && item.children.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    } else {
      item.onClick?.();
      onItemClick?.(item);
    }
  };

  return (
    <React.Fragment key={item.id}>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <StyledListItemButton
          level={level}
          selected={item.selected}
          disabled={item.disabled}
          onClick={handleClick}
        >
          {item.icon && (
            <ListItemIcon
              sx={{
                minWidth: 56,
                mr: styleVariant === 'mini' || styleVariant === 'rail' ? 'auto' : 3,
                justifyContent: styleVariant === 'mini' || styleVariant === 'rail' ? 'center' : 'flex-start',
              }}
            >
              {item.icon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={item.label}
            sx={{
              opacity: styleVariant === 'mini' ? 0 : 1,
              transition: 'opacity 0.2s',
            }}
          />
          {hasChildren && (
            <Box sx={{ opacity: styleVariant === 'mini' ? 0 : 1 }}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </Box>
          )}
          {item.badge && (
            <Box
              sx={{
                bgcolor: 'error.main',
                color: 'error.contrastText',
                borderRadius: '50%',
                minWidth: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                ml: 1,
                opacity: styleVariant === 'mini' ? 0 : 1,
              }}
            >
              {item.badge}
            </Box>
          )}
        </StyledListItemButton>
      </ListItem>

      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children!.map((child) =>
              renderDrawerItem(child, level + 1, onItemClick, styleVariant)
            )}
          </List>
        </Collapse>
      )}

      {item.divider && <Divider sx={{ my: 1 }} />}
    </React.Fragment>
  );
};

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  onToggle,
  variant = 'temporary',
  styleVariant = 'default',
  anchor = 'left',
  items,
  header,
  footer,
  showCloseButton = true,
  width = DRAWER_WIDTH,
  autoCloseMobile = true,
  onItemClick,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const handleItemClick = (item: DrawerItem) => {
    onItemClick?.(item);
    if (autoCloseMobile && isMobile && variant === 'temporary') {
      onClose?.();
    }
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {(header || showCloseButton) && (
        <DrawerHeader>
          {header && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              {header}
            </Box>
          )}
          {showCloseButton && onClose && (
            <IconButton onClick={onClose}>
              {anchor === 'right' ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          )}
        </DrawerHeader>
      )}

      {(header || showCloseButton) && <Divider />}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List>
          {items.map((item) => renderDrawerItem(item, 0, handleItemClick, styleVariant))}
        </List>
      </Box>

      {footer && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            {footer}
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <StyledDrawer
      variant={variant}
      styleVariant={styleVariant}
      open={open}
      onClose={onClose}
      anchor={anchor}
      {...props}
      sx={{
        width: open ? width : (styleVariant === 'mini' ? MINI_DRAWER_WIDTH : 0),
        '& .MuiDrawer-paper': {
          width: styleVariant === 'mini' ? MINI_DRAWER_WIDTH : width,
        },
        ...props.sx,
      }}
    >
      {drawerContent}
    </StyledDrawer>
  );
}; 